<template>
  <div class="reassign-ownership-page">
    <b-loading
      :is-full-page="true"
      :active.sync="isLoading"
      :can-cancel="false"
    ></b-loading>

    <header class="reassign-header">
      <p class="reassign-title">Reassign Ownership</p>
      <p class="reassign-subtitle">{{ reassignSubtitle }}</p>
    </header>

    <main class="reassign-main">
      <section class="reassign-body">
        <reassign-job
          ref="reassignJob"
          @siteSelected="handleSiteSelected"
          @userSelected="handleUserSelected"
          @selectionChanged="handleSelectionChanged"
          :isReassignJobsActive="true"
          :currentOwner="owner"
          :enableGranularSelection="true"
          :ownerId="owner ? owner._id : ''"
        >
          <template #top-actions>
            <div class="top-actions">
              <b-button @click="handleCancel">Cancel</b-button>
              <b-button
                type="is-primary"
                :disabled="!canSubmit"
                @click="handleConfirm"
                >Reassign</b-button
              >
            </div>
          </template>
        </reassign-job>
      </section>
    </main>
  </div>
</template>

<script>
import ReassignJob from '../layout/ReassignJob';
import store from '../../store/index';

export default {
  name: 'ReassignOwnershipPage',
  components: {
    ReassignJob,
  },
  data() {
    return {
      isLoading: false,
      owner: null,
      selectedSite: null,
      isSiteReassignSelected: false,
      isUserReassignSelected: false,
      userToReassign: null,
      selectedReassignIds: [],
    };
  },
  computed: {
    ownerName() {
      if (!this.owner) return '';
      return this.owner.fullname || this.owner._id || '';
    },
    reassignSubtitle() {
      if (this.userToReassign && this.selectedSite) {
        const destinationUser =
          this.userToReassign.fullname || this.userToReassign._id;
        const destinationSite =
          this.selectedSite.siteName || this.selectedSite.locationName;
        return `Reassigning items owned by "${this.ownerName}" to ${destinationUser} in site ${destinationSite}.`;
      }

      return `Reassigning items owned by "${this.ownerName}"`;
    },
    canSubmit() {
      return (
        this.isSiteReassignSelected &&
        this.isUserReassignSelected &&
        this.selectedReassignIds.length > 0
      );
    },
  },
  async beforeRouteEnter(to, {}, next) {
    const { id } = to.params;

    await store.dispatch('getUserProfile', id);
    await store.dispatch('getUserOwnedReassignItems', { userId: id });

    next();
  },
  created() {
    this.owner = { ...this.$store.state.user };
  },
  methods: {
    handleSiteSelected(site) {
      this.isSiteReassignSelected = true;
      this.selectedSite = site;
      this.isUserReassignSelected = false;
      this.userToReassign = null;
    },
    handleUserSelected(user) {
      this.isUserReassignSelected = true;
      this.userToReassign = user;
    },
    handleSelectionChanged(payload) {
      this.selectedReassignIds = payload?.selectedJobIds || [];
    },
    handleCancel() {
      this.$router.push({
        name: 'edit-user',
        params: { id: this.$route.params.id },
      });
    },
    async handleConfirm() {
      if (!this.canSubmit) return;

      const DTO = {
        newOwner: this.userToReassign,
        oldOwner: this.owner,
        jobIds: this.selectedReassignIds,
      };

      try {
        this.isLoading = true;
        await this.$store.dispatch('reassignJobsByUser', DTO);

        this.$store.dispatch(
          'setSuccessMessage',
          this.$t('edit_user_profile.job_reassignment_success_message')
        );

        await this.$store.dispatch('getUserOwnedReassignItems', {
          userId: this.owner._id,
          forceRefresh: true,
        });

        this.resetState();
        this.isLoading = false;

        this.$router.push({
          name: 'edit-user',
          params: { id: this.$route.params.id },
        });
      } catch (err) {
        console.error('reassign job error inside admin page', err);
        this.isLoading = false;

        this.$store.dispatch(
          'setErrorMessage',
          this.$t('edit_user_profile.job_reassignment_fail_message')
        );
      }
    },
    resetState() {
      this.selectedSite = null;
      this.isSiteReassignSelected = false;
      this.isUserReassignSelected = false;
      this.userToReassign = null;
      this.selectedReassignIds = [];

      if (this.$refs.reassignJob) {
        this.$refs.reassignJob.refresh();
      }
    },
  },
};
</script>

<style lang="scss">
.reassign-ownership-page {
  padding: 16px;
  height: calc(100vh - 96px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.reassign-header {
  margin-bottom: 12px;
  flex-shrink: 0;
}

.reassign-title {
  font-size: 28px;
  font-weight: 600;
}

.reassign-subtitle {
  margin-top: 4px;
}

.reassign-main {
  min-height: 0;
  flex: 1;
  overflow: hidden;
}

.reassign-body {
  height: 100%;
  overflow: hidden;
}

.top-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
